import { InvestigationAssetType } from '@customer/store/apis/investigationsApi';
import { useAppDispatch } from '@customer/store/hooks';
import { openSidePanel, SidePanel } from '@customer/store/slices/globalSlice';
import {
  InvestigationEnitityIdProps,
  setInvestigationAssetVisibility,
  useSelectAssetControl,
} from '@customer/store/slices/investigationsSlice';
import { MouseEvent as ReactMouseEvent, useCallback } from 'react';

export const isAssetControlVisible = (type: InvestigationAssetType, visible?: boolean): boolean => {
  // Use the visibility prop if it has been defined, otherwise calculate default visibility.
  return visible ?? (type === InvestigationAssetType.Nearby ? false : true);
};

interface AssetControlProps extends Partial<InvestigationEnitityIdProps> {
  assetId: number;
  type: InvestigationAssetType;
  noData: boolean;
}

interface AssetControlReturn {
  isVisible: boolean;
  isDisabled: boolean;
  onToggleVisibility: (e: ReactMouseEvent) => void;
  onShowSidePanel: () => void;
}

export const useAssetControl = ({
  assetId,
  type,
  siteId,
  investigationId,
  noData,
}: AssetControlProps): AssetControlReturn => {
  const dispatch = useAppDispatch();

  const isInvestigationLoaded =
    typeof siteId !== 'undefined' && typeof investigationId !== 'undefined';

  const assetControl = useSelectAssetControl({
    siteId,
    investigationId,
    assetId,
  });

  // Default to visible.
  const isVisible = assetControl?.visible ?? true;

  const onToggleVisibility = useCallback((): void => {
    if (isInvestigationLoaded) {
      dispatch(
        setInvestigationAssetVisibility({
          siteId,
          investigationId,
          assetId,
          visible: !isVisible,
        }),
      );
    }
  }, [dispatch, isInvestigationLoaded, isVisible, assetId, siteId, investigationId]);

  const onShowSidePanel = useCallback((): void => {
    dispatch(
      openSidePanel({
        sidePanel: SidePanel.InvestigationAsset,
        modelId: assetId.toString(),
      }),
    );
  }, [assetId, dispatch]);

  return {
    isVisible,
    isDisabled: !isInvestigationLoaded || noData,
    onToggleVisibility: isInvestigationLoaded && !noData ? onToggleVisibility : () => undefined,
    onShowSidePanel: isInvestigationLoaded ? onShowSidePanel : () => undefined,
  };
};
